/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1747872000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu May 22 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "tradedesk",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Postback events credentials",
          "depth": 1,
          "id": "postback-events-credentials"
        }, {
          "value": "Conversion events credentials",
          "depth": 1,
          "id": "conversion-events-credentials"
        }, {
          "value": "Offline conversion events credentials",
          "depth": 1,
          "id": "offline-conversion-events-credentials"
        }, {
          "value": "CRM data segment credentials",
          "depth": 1,
          "id": "crm-data-segment-credentials"
        }, {
          "value": "First-party data segment credentials",
          "depth": 1,
          "id": "first-party-data-segment-credentials"
        }, {
          "value": "Third-party data segment credentials",
          "depth": 1,
          "id": "third-party-data-segment-credentials"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing postback events",
          "depth": 1,
          "id": "syncing-postback-events"
        }, {
          "value": "Syncing real-time conversion events",
          "depth": 1,
          "id": "syncing-real-time-conversion-events"
        }, {
          "value": "Syncing offline conversion events",
          "depth": 1,
          "id": "syncing-offline-conversion-events"
        }, {
          "value": "Syncing CRM data segments",
          "depth": 1,
          "id": "syncing-crm-data-segments"
        }, {
          "value": "First and third party data segments",
          "depth": 1,
          "id": "first-and-third-party-data-segments"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      a: "a",
      h4: "h4",
      img: "img"
    }, _provideComponents(), props.components), {EventSyncWarning, MatchBoosterDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Power your The Trade Desk platform with up-to-date data straight from your data warehouse. Sync first-party data or use highly customizable postback URLs to handle data your way. Keeping your data updated equips your team with the necessary tools they need to deliver a top of the line advertising experience."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Postback events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to The Trade Desk via a postback URL"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Real-time conversion events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to The Trade Desk as real-time conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Offline conversion events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to The Trade Desk as offline conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "CRM data segments"
              })
            }), _jsx(_components.td, {
              children: "Sync your Customer relationship management (CRM) data from any source to The Trade Desk data segments"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "First-party data segments"
              })
            }), _jsx(_components.td, {
              children: "Sync your first-party data from any source to The Trade Desk first-party data segments"
            }), _jsx(_components.td, {
              children: "Add"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Third-party data segments"
              })
            }), _jsx(_components.td, {
              children: "Sync your third-party data from any source to The Trade Desk third-party data segments"
            }), _jsx(_components.td, {
              children: "Add"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "You need to enter different credentials depending on whether you're syncing events, CRM data segments, or first-party data segments. If you plan to use multiple sync types, enter the necessary credentials for all of them. Entering all the required credentials lets you use the same destination setup for every Trade Desk sync."
      }), "\n", _jsxs(_components.p, {
        children: ["Once you have entered your inputs, press the ", _jsx(_components.strong, {
          children: "Test Connection"
        }), " button to check you have inputted valid credentials."]
      }), "\n", _jsx(_components.h3, {
        id: "postback-events-credentials",
        children: "Postback events credentials"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to sync postback events, you need to enter a root postback URL."
      }), "\n", _jsx(_components.h3, {
        id: "conversion-events-credentials",
        children: "Conversion events credentials"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to sync real-time conversion events, you need the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Advertiser ID"
        }), "\n", _jsx(_components.li, {
          children: "Advertiser secret key"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "offline-conversion-events-credentials",
        children: "Offline conversion events credentials"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to sync offline conversion events, you need the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Advertiser ID"
        }), "\n", _jsx(_components.li, {
          children: "Advertiser secret key"
        }), "\n", _jsx(_components.li, {
          children: "Offline data provider ID"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you would like to create offline tracking tags through Hightouch, an ", _jsx(_components.strong, {
          children: "API token"
        }), " is also required."]
      }), "\n", _jsx(_components.h3, {
        id: "crm-data-segment-credentials",
        children: "CRM data segment credentials"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to sync data to your CRM data segments, you need the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Advertiser ID"
        }), "\n", _jsxs(_components.li, {
          children: ["Environment - select either your ", _jsx(_components.code, {
            children: "Production"
          }), " or ", _jsx(_components.code, {
            children: "Sandbox"
          }), " environment"]
        }), "\n", _jsx(_components.li, {
          children: "API token"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can create an API token by logging onto The Trade Desk platform using your API credentials. These API credentials can be provided by your account manager. Navigate to the ", _jsx(_components.strong, {
          children: "Manage API Tokens"
        }), " option under the user profile menu at the top-right of The Trade Desk UI. Then either create a Long- or Short-Lived API token. Once you have copied the API token, navigate back to Hightouch and paste the API token."]
      }), "\n", _jsx(_components.h3, {
        id: "first-party-data-segment-credentials",
        children: "First-party data segment credentials"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to sync data to your first-party data segments, you need the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Advertiser ID"
        }), "\n", _jsx(_components.li, {
          children: "Environment - select a server location of your choice"
        }), "\n", _jsx(_components.li, {
          children: "Advertiser secret key"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can obtain your decoded advertiser secret key for uploading first-party data from your advertiser preferences page in The Trade Desk UI. If you can't find your secret key, contact your Account Manager. When you send data, the included secret key is validated against the secret key the platform has on file for the Advertiser ID."
      }), "\n", _jsx(_components.h3, {
        id: "third-party-data-segment-credentials",
        children: "Third-party data segment credentials"
      }), "\n", _jsx(_components.p, {
        children: "For Hightouch to sync data to your third-party data segments, you need the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Data provider ID"
        }), "\n", _jsx(_components.li, {
          children: "Environment - select a server location of your choice"
        }), "\n", _jsx(_components.li, {
          children: "Data provider secret key"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can obtain your decoded data provider secret key for uploading third-party data from your advertiser preferences page in The Trade Desk UI. If you can't find your secret key, contact your Account Manager. When you send data, the included secret key is validated against the secret key the platform has on file for the data provider ID."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["When setting up for first-party data segments, upload your data to the\nhostname closest to the location of the users targeted by your campaign. Using\nthe closest hostname reduces latency. After a short delay, Hightouch\nreplicates the data to other data centers globally. Please don't upload the\nsame data to more than one data center. Certain data types have exceptions\nincluding how they replicate to countries with specific privacy regulations.\nRefer to ", _jsx(_components.a, {
            href: "https://api.thetradedesk.com/v3/portal/data/doc/DataEnvironments",
            children: "The Trade Desk\ndocs"
          }), " for\nmore details."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "syncing-postback-events",
        children: "Syncing postback events"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports inserting impression data as events to The Trade Desk."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to The Trade Desk event custom fields."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-tradedeskPostback-2.png",
          alt: "Mapping model columns to The Trade Desk custom fields in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "syncing-real-time-conversion-events",
        children: "Syncing real-time conversion events"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to The Trade Desk Ads as real-time conversion events."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync event properties via field mappings."
      }), "\n", _jsx(_components.h4, {
        id: "custom-mappings",
        children: "Custom mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can also map additional custom data using The Trade Desk's ten sequentially numbered custom dynamic properties to provide additional conversion metadata (", _jsx(_components.code, {
          children: "t1"
        }), "-", _jsx(_components.code, {
          children: "t10"
        }), ")."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "syncing-offline-conversion-events",
        children: "Syncing offline conversion events"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to The Trade Desk as ", _jsx(_components.a, {
          href: "https://partner.thetradedesk.com/v3/portal/data/doc/post-providerapi-offlineconversion",
          children: "offline conversion events."
        })]
      }), "\n", _jsx(_components.h4, {
        id: "offline-tracking-tags",
        children: "Offline tracking tags"
      }), "\n", _jsxs(_components.p, {
        children: ["An ", _jsx(_components.a, {
          href: "https://partner.thetradedesk.com/v3/portal/data/doc/DataOfflineMeasurement#step1",
          children: "offline tracking tag"
        }), " is required for each offline conversion event in The Trade Desk. This offline tracking tag must be associated with the same offline data provider ID that is in the destination configuration."]
      }), "\n", _jsx(_components.p, {
        children: "Select whether you'd like to use the same tracking tag for all rows in your sync, or map the tracking tag ID from a column in your source."
      }), "\n", _jsx(_components.p, {
        children: "You can optionally create a new offline tracking tag with the former option. Provide the name for your new offline tracking tag, and ensure that you have provided an API token in your destination configuration."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["At least one ID and the timestamp is required for each offline event. If you provide an ", _jsx(_components.code, {
          children: "ImpressionId"
        }), ", no other IDs are required. Refer to The Trade Desk's ", _jsx(_components.a, {
          href: "https://partner.thetradedesk.com/v3/portal/data/doc/post-providerapi-offlineconversion#supported-ids",
          children: "documentation for supported ID types"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync additional event properties via field mappings."
      }), "\n", _jsx(_components.h4, {
        id: "custom-mappings-1",
        children: "Custom mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can also map additional custom data using The Trade Desk's ten sequentially numbered custom dynamic properties to provide additional conversion metadata (", _jsx(_components.code, {
          children: "t1"
        }), "-", _jsx(_components.code, {
          children: "t10"
        }), ")."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-crm-data-segments",
        children: "Syncing CRM data segments"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports inserting and removing your data to your CRM data segments in The Trade Desk. CRM data is first-party data that includes all client information collected and shared across your organization."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an CRM data segment, select model columns and the corresponding Trade Desk fields. You can match on any of the following Trade Desk fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Email (SHA256)"
        }), "\n", _jsx(_components.li, {
          children: "Phone"
        }), "\n", _jsx(_components.li, {
          children: "Phone (SHA256)"
        }), "\n"]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "The Trade Desk segments"
      }), "\n", _jsx(_components.h4, {
        id: "creating-a-new-segment",
        children: "Creating a new segment"
      }), "\n", _jsx(_components.p, {
        children: "In your sync configuration, you can tell Hightouch to create a new data segment for you. You can optionally assign a custom name to your segment. If no name is provided, Hightouch defaults to using your model name."
      }), "\n", _jsx(_components.p, {
        children: "You also need to select which region you would like to create your CRM data segment in. This is based on the origin of the PII."
      }), "\n", _jsx(_components.h4, {
        id: "using-an-existing-segment",
        children: "Using an existing segment"
      }), "\n", _jsx(_components.p, {
        children: "Additionally, instead of creating a new data segment Hightouch can also sync users to existing segments that are already populated in your The Trade Desk account."
      }), "\n", _jsx(_components.h4, {
        id: "pii-hashing",
        children: "PII hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["You can enable PII hashing if you wish for Hightouch to detect whether your data requires normalization and hashing based on the chosen record mapping. For example, if you select ", _jsx(_components.code, {
          children: "Email (SHA256)"
        }), " and the model column selected with the email data is unhashed, Hightouch hashes the value for you."]
      }), "\n", _jsx(_components.h4, {
        id: "ttl",
        children: "TTL"
      }), "\n", _jsx(_components.p, {
        children: "Data synced into The Trade Desk has a Time-To-Live (TTL) value. This is the amount of time the data remains active in the segment. The default recommended value by The Trade Desk is 90 days. If you wish to customize this interval, you can choose a column or enter the number of days (between 0 and 180) you want your data to live in the segment for."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Setting this value to 0 will mark synced rows for deletion. If all rows in your segment have a TTL of 0, your segment will be deleted, and subsequent sync runs may error."
        })
      }), "\n", _jsx(_components.h4, {
        id: "full-resync-interval",
        children: "Full Resync interval"
      }), "\n", _jsx(_components.p, {
        children: "This is the interval Hightouch will use to schedule a full resync of your data to The Trade Desk. The Trade Desk recommends a full data refresh every 30 days, however you can choose to retain hashed data for up to 90 days or process it once."
      }), "\n", _jsx(_components.h3, {
        id: "first-and-third-party-data-segments",
        children: "First and third party data segments"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports inserting your data to first and third party data segments in The Trade Desk."
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to The Trade Desk on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Trade Desk GUID: The Trade Desk 36-character GUID (including dashes) for a user."
        }), "\n", _jsx(_components.li, {
          children: "Raw Device ID: the raw device ID for this user, sent in 36-character GUID format, including dashes. Use iOS IDFA or Android's AAID."
        }), "\n", _jsx(_components.li, {
          children: "UID2: The 256-bit UID2 as a base64-encoded string."
        }), "\n", _jsx(_components.li, {
          children: "UID2Token: The encrypted UID2 token."
        }), "\n", _jsx(_components.li, {
          children: "EUID: The user's European Unified ID as a 44-character base64-encoded SHA-256 string. This ID is case-sensitive."
        }), "\n", _jsx(_components.li, {
          children: "EUIDToken String The encrypted EUID token."
        }), "\n", _jsxs(_components.li, {
          children: ["RampID: The 49-character or 70-character RampID, previously known as IdentityLink. This must be a RampID from LiveRamp that is mapped specifically for The Trade Desk. For details about mapping a RampID, see ", _jsx(_components.a, {
            href: "https://sidecar.readme.io/docs/getting-started",
            children: "LiveRamp documentation"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["ID5: Data providers holding these IDs should engage with ID5 directly to obtain the necessary decryption keys. For details see the ", _jsx(_components.a, {
            href: "https://id5.io/",
            children: "ID5 site"
          }), ". ", _jsx(_components.strong, {
            children: "NOTE: The ID5 ID must be in decrypted form."
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["netID: A 70-character base64-encoded string. For details see the ", _jsx(_components.a, {
            href: "https://developerzone.netid.dev/latest/devportal/",
            children: "netID Developer Portal"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["FirstId: A first-party cookie typically set by publishers in France. For details see the ", _jsx(_components.a, {
            href: "https://www.first-id.fr/",
            children: "First-id site"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Hightouch provides the ability to convert hashed emails and phone identifiers to UID2 identifiers during sync execution. Contact your rep to inquire about eligibility for this feature."
        })
      }), "\n", _jsx(_components.h4, {
        id: "creating-a-new-segment-1",
        children: "Creating a new segment"
      }), "\n", _jsx(_components.p, {
        children: "In your sync configuration, you can tell Hightouch to create a new data segment for you. You can optionally assign a custom name to your segment. If no name is provided, Hightouch defaults to using your model name."
      }), "\n", _jsx(_components.h4, {
        id: "using-an-existing-segment-1",
        children: "Using an existing segment"
      }), "\n", _jsx(_components.p, {
        children: "Instead of creating a new data segment Hightouch can also sync users to existing segments already populated in your The Trade Desk account."
      }), "\n", _jsx(_components.h4, {
        id: "full-resync-interval-1",
        children: "Full resync interval"
      }), "\n", _jsx(_components.p, {
        children: "Data synced into The Trade Desk has a Time-To-Live (TTL) value. This is the amount of time the data remains active in the segment. The default value recommended value by The Trade Desk is 30 days. If you wish to customize this interval, enter the number of days you want your data to live in the segment for. After it expires, Hightouch triggers a full resync on your next sync run to keep your data refreshed in The Trade Desk."
      }), "\n", _jsx(_components.h4, {
        id: "data-taxonomy",
        children: "Data taxonomy"
      }), "\n", _jsxs(_components.p, {
        children: ["When creating a new third party segment, you can create a corresponding element in your third-party data taxonomy in The TradeDesk.\nTo create a new data element, you will have to update your ", _jsx(_components.strong, {
          children: "destination"
        }), " configuration with a long-lived API token.\nProvide the following details when creating a new data element:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Provider Element ID: the identifier for your segment. Hightouch will default to the ", _jsx(_components.strong, {
            children: "sync ID"
          }), " by default."]
        }), "\n", _jsx(_components.li, {
          children: "Parent Element ID: the identifier of the folder where this segment should live. Defaults to Root."
        }), "\n", _jsx(_components.li, {
          children: "Display name"
        }), "\n", _jsx(_components.li, {
          children: "Description"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can optionally also provide a data rate for this segment to make it available to partners or advertisers.\nYou will need your brand ID. Contact your rep at The TradeDesk if you'd like to use this feature for your segment."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch will confirm the data segment and data rate batch are approved, but will ", _jsx(_components.strong, {
          children: "not"
        }), " wait for processing to complete. We recommend checking the status of your segment and data rate batch in The TradeDesk UI to confirm the completion."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsx(_components.p, {
        children: "Below only applies to the CRM data segment sync type."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch retrieves the audience metadata from The Trade Desk in real-time. The matched user\ncount reflects the active IDs based on a ", _jsx(_components.strong, {
          children: "7-day"
        }), " look back window where the ID came up for bidding."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
